import styled from 'styled-components';
import { scrollbar } from 'common/styles/scrollbar';

export const ScrollArea = styled.div`
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  ${scrollbar}
`;

export const Content = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: ${props => (props.compact ? '0 10px 8px' : '0 12px 12px')};
  box-sizing: border-box;
  width: 100%;
`;

export const OrgSummary = styled.div`
  flex-shrink: 0;
  border: 1px solid #e8eaed;
  border-radius: 6px;
  padding: ${props => (props.compact ? '8px 10px' : '12px')};
  margin-bottom: ${props => (props.compact ? '8px' : '12px')};
  background: #fafbfc;
`;

export const SummaryHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${props => (props.compact ? '8px' : '12px')};
  margin-bottom: ${props => (props.compact ? '6px' : '8px')};
`;

export const SummaryLabel = styled.div`
  font-size: ${props => (props.compact ? '11px' : '12px')};
  font-weight: 600;
  color: #374151;
`;

export const SummaryValue = styled.div`
  font-size: ${props => (props.compact ? '20px' : '22px')};
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
`;

export const Meta = styled.div`
  font-size: ${props => (props.compact ? '10px' : '11px')};
  color: #6b7280;
  margin-top: ${props => (props.compact ? '4px' : '8px')};
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  /* Tight row gap so the rare all-six-conditions case wraps to two compact
     rows rather than a loose stack. */
  gap: ${props => (props.compact ? '4px 8px' : '5px 10px')};
  margin-bottom: ${props => (props.compact ? '8px' : '10px')};
  flex-shrink: 0;
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => (props.compact ? '4px' : '5px')};
  font-size: ${props => (props.compact ? '10px' : '11px')};
  color: #4b5563;
`;

export const LegendSwatch = styled.span`
  width: ${props => (props.compact ? '8px' : '9px')};
  height: ${props => (props.compact ? '8px' : '9px')};
  border-radius: 2px;
  background: ${props => props.color};
  flex-shrink: 0;
`;

export const SiteList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

/* Optional titled-column header aligned to the row's bar + value columns, so a
   widget can label what each number means (e.g. Health / Critical). Widths mirror
   the bar (inRowWidth) and SiteValue so the labels sit exactly over their data. */
export const ColumnHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => (props.compact ? '8px' : '10px')};
  padding: 0 0 4px;
  border-bottom: 1px solid #e8eaed;
  margin-bottom: 2px;
`;

export const HeaderName = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const headerLabel = `
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  /* #6b7280 clears WCAG AA (4.6:1) for this small label text. */
  color: #6b7280;
  white-space: nowrap;
`;

export const HeaderBarLabel = styled.div`
  flex: 0 0 ${props => (props.compact ? '72px' : '104px')};
  text-align: center;
  ${headerLabel}
`;

export const HeaderValueLabel = styled.div`
  flex: 0 0 ${props => (props.compact ? '54px' : '74px')};
  text-align: right;
  ${headerLabel}
`;

/* One row per site: name (truncates) · inline bar · value. Rows are separated
   by a hairline rather than stacked two-line blocks, so many sites stay
   scannable and roughly twice as many fit before scrolling.

   When `interactive`, the whole row opens the site — the name and caret are
   only the affordance, so the bar and value are clickable target too. */
export const SiteRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => (props.compact ? '8px' : '10px')};
  padding: ${props => (props.compact ? '4px 6px' : '5px 6px')};
  margin: 0 -6px;
  border-radius: 4px;
  border-bottom: 1px solid #f2f3f5;
  cursor: ${props => (props.interactive ? 'pointer' : 'default')};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f7f9fb;
  }

  ${props => (props.interactive ? `
    &:hover .org-site-name {
      color: #1a73e8;
      text-decoration: underline;
    }
    &:hover .org-site-caret {
      opacity: 1;
      color: #1a73e8;
    }
    /* Focus lands on the name button, so mirror the hover state onto the row
       to show keyboard users which row they are about to open. */
    &:focus-within {
      background: #f7f9fb;
    }
    &:focus-within .org-site-caret {
      opacity: 1;
    }
  ` : '')}
`;

export const SiteName = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 2px;
  overflow: hidden;
  /* Dial the site name down from the inherited card size so it sits closer to
     the value and hands more of the row width to the breakdown bar. */
  font-size: ${props => (props.compact ? '10px' : '11px')};
  /* Dark grey, medium weight — the bold value is the emphasis; the name just
     labels the row. */
  color: #6b7280;
  font-weight: 500;
`;

export const SiteValue = styled.div`
  font-size: ${props => (props.compact ? '10px' : '11px')};
  font-weight: 700;
  color: ${props => props.tone || '#1f2937'};
  /* Fixed-width value column so a wide value (e.g. "73 (50.7%)") can't grow the
     cell and shove its row's bar out of the column — the bar stays aligned down
     the list. A rare over-wide value overflows left into the gap, not the bar. */
  flex: 0 0 ${props => (props.compact ? '54px' : '74px')};
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const barHeight = (props) => {
  if (props.compact) return '6px';
  if (props.medium) return '8px';
  return '10px';
};

const barRadius = (props) => {
  if (props.compact) return '3px';
  if (props.medium) return '4px';
  return '5px';
};

const inRowWidth = (props) => {
  if (!props.inRow) return '100%';
  return props.compact ? '72px' : '104px';
};

export const ShareBar = styled.div`
  display: flex;
  width: ${inRowWidth};
  flex: ${props => (props.inRow ? '0 0 auto' : '1 1 auto')};
  height: ${barHeight};
  border-radius: ${barRadius};
  overflow: hidden;
  background: #eef0f2;
`;

export const ShareFill = styled.div`
  height: 100%;
  width: ${props => props.pct}%;
  background: ${props => props.color};
  min-width: ${props => (props.pct > 0 ? '2px' : 0)};
`;

export const SegBar = styled.div`
  display: flex;
  width: ${inRowWidth};
  flex: ${props => (props.inRow ? '0 0 auto' : '1 1 auto')};
  height: ${barHeight};
  border-radius: ${barRadius};
  overflow: hidden;
  background: #eef0f2;
`;

export const Segment = styled.div`
  height: 100%;
  width: ${props => props.pct}%;
  background: ${props => props.color};
  min-width: ${props => (props.pct > 0 ? '2px' : 0)};
`;

export const EmptyText = styled.div`
  color: #6b7280;
  font-size: ${props => (props.compact ? '11px' : 'inherit')};
`;

/* Collapses the trailing all-zero sites into a single muted, toggleable row so
   the list keeps focus on sites that have something to act on. */
export const ZeroToggle = styled.button`
  display: block;
  width: calc(100% + 12px);
  margin: 2px -6px 0;
  padding: ${props => (props.compact ? '5px 6px' : '6px 6px')};
  background: none;
  border: none;
  border-radius: 4px;
  font-family: inherit;
  font-size: ${props => (props.compact ? '10px' : '11px')};
  font-weight: 500;
  color: #9aa1a8;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f7f9fb;
    color: #6b7280;
  }
  &:focus-visible {
    outline: 2px solid #1a73e8;
    outline-offset: 2px;
  }
`;

export const SecondaryValue = styled.span`
  font-weight: 500;
  color: #6b7280;
`;
